/**
 * Kampagnernes produkt-regler ([D-354], [D-355], Charlie 01-10-2026): de
 * regeltyper serveren kan matche, deres navne i UI'et, reglen for hvilken
 * regel der gælder når en vare rammes af flere, og KONTRAKTEN for kampagnens
 * prisliste.
 *
 * Prislisten er grundlaget for den kørsel der sætter kampagnepriserne i BC ved
 * kampagnens start og fjerner dem ved slut. Kørslen bygges af DITCO
 * (Sebastian), ikke i Cockpit (Charlie 01-10-2026); den læser
 * `GET /api/campaigns/:id/prices` og skal bruge `pickWinningRule` herfra, hvis
 * den selv regner, så Cockpits prognose, måling og BC aldrig er uenige om
 * hvilken rabat en vare har. §20 gælder: enhver skrivning til BC Production
 * kræver Charlies OK pr. operation (whitelist-lag, Sandbox først).
 */

export const CAMPAIGN_RULE_TYPES = [
  'item_no',
  'search_string',
  'name_prefix',
  'supplier_no',
  'supplier_name',
  'item_group',
  'all_items',
] as const;
export type CampaignRuleType = (typeof CAMPAIGN_RULE_TYPES)[number];

/** Navnet i UI'et. ÉT sted, så formen, listen og Preview siger det samme. */
export const CAMPAIGN_RULE_TYPE_LABELS: Record<CampaignRuleType, string> = {
  item_no: 'Varenummer',
  search_string: 'Varenavn indeholder',
  name_prefix: 'Varenavn starter med',
  supplier_no: 'Leverandørnr.',
  supplier_name: 'Leverandørnavn',
  item_group: 'Varegruppe/bogf.gruppe',
  all_items: 'Alle varer',
};

/** Regeltyper der ikke har en værdi (reglen rammer alt, fluebenene afgrænser). */
export function campaignRuleTypeNeedsValue(t: string): boolean {
  return t !== 'all_items';
}

/** En regels bud på en vare: reglens id og dens rabat-% (null = ingen rabat angivet). */
export interface CampaignRuleCandidate {
  ruleId: number;
  discountPercent: number | null;
}

/**
 * Den regel der gælder for en vare, når flere regler rammer den (Charlie
 * 01-10-2026): den HØJESTE rabat vinder, ligesom BC vælger den bedste pris.
 * Ved lige rabat vinder den ældste regel (laveste id), så valget er stabilt.
 * En regel uden rabat-% taber til enhver regel med.
 */
export function pickWinningRule<T extends CampaignRuleCandidate>(candidates: readonly T[]): T | null {
  let best: T | null = null;
  for (const c of candidates) {
    if (!best) {
      best = c;
      continue;
    }
    const a = c.discountPercent ?? -1;
    const b = best.discountPercent ?? -1;
    if (a > b || (a === b && c.ruleId < best.ruleId)) best = c;
  }
  return best;
}

/** Én linje i kampagnens prisliste. Beløb er ekskl. moms pr. varens basisenhed. */
export interface CampaignPriceLine {
  itemNo: string;
  name: string;
  /** Den regel der gælder (højeste rabat vinder). */
  ruleId: number;
  discountPercent: number;
  /**
   * Varekortets salgspris ekskl. moms; null = ukendt. ⚠ For fliser kan BC's
   * prislister regne i m², selv om basisenheden er STK; kontrollér enheden mod
   * BC's prisliste før en pris skrives (bfAPIProductSalesPriceList: stk for
   * varer, m² for fliser). Rabat-% er den sikre størrelse at overføre.
   */
  unitPrice: number | null;
  /** unitPrice x (1 - rabat); null når salgsprisen er ukendt. */
  campaignPrice: number | null;
  /** Varens basisenhed i BC (fx STK, M2). Prisen gælder pr. denne enhed. */
  unit: string | null;
  /**
   * [D-357]/[D-358]: højst så mange enheder sælges til `discountPercent`, når
   * reglen der gælder, er en lagerregel (Kun disponibel
   * eller kampagnens Alle regler kun på disponibelt lager): beholdning minus
   * antal på salgsordrer ved Cockpits seneste lagersynk (`quantityMeasuredAt`),
   * aldrig under 0. null = intet loft (reglen har intet lagerkrav).
   */
  maxQuantity: number | null;
  /**
   * [D-358]: rabatten for antallet ud over `maxQuantity`: den bedste regel
   * uden lagerkrav der også rammer varen (fx SS304 -40 % efter de disponible
   * stk til -70 %). null = resten til normalpris. Altid null når `maxQuantity`
   * er null.
   */
  afterMaxQuantity: { ruleId: number; discountPercent: number; campaignPrice: number | null } | null;
  /**
   * [D-361]: linjen er et restparti (reglen har Kun restparti). `itemNo` er
   * da restpartiets varenummer. null = en almindelig vare.
   */
  restparti: CampaignRestpartiLine | null;
  /**
   * [D-400]: varen har i forvejen en stående rabat DYBERE end reglens sats, og
   * beholder den: `discountPercent` og `campaignPrice` er den stående rabat og
   * den gældende pris, `maxQuantity`/`afterMaxQuantity` er null (den stående
   * rabatlinje har intet antalsloft), og DITCO skal hverken oprette kopi eller
   * ændre pris for varen. Undtagelse ([D-466]): `standing.setOnCopy` = linjen
   * er en eksisterende -R-kopi uden eget tilbud, og kopien skal have linjens
   * sats. null = en almindelig kampagnelinje.
   */
  standing: CampaignStandingDiscount | null;
}

/**
 * [D-400] En stående rabat: en gældende rabat varen har i forvejen, uafhængigt
 * af kampagnen (en aktiv rabatlinje i BC's salgsprislister, fx restpartiernes
 * 65 % i S00001, eller webshoppens aktive tilbudspris).
 */
export interface CampaignStandingDiscount {
  /** Den stående rabat i % af normalprisen. */
  discountPercent: number;
  /** Kilden: en aktiv rabatlinje i BC's salgsprislister, eller webshoppens gældende tilbudspris. */
  source: 'bc_prisliste' | 'webshop';
  /** BC-prislisten rabatlinjen står i (fx S00001); null for webshop-kilden. */
  priceListCode: string | null;
  /**
   * [D-464]: varen har haft et tilbud inden for de 30 dage før kampagnestart og
   * beholder sin tilbudspris uanset dybde (markedsføringsloven). 0 % = tilbuddet
   * er sluttet før start, og varen står til normalpris.
   */
  recent?: boolean;
  /**
   * [D-466]: linjen er en -R-kopi, der findes i BC, men kopien har ikke selv
   * tilbuddet (den arver originalens). DITCO sætter linjens sats på kopien, så
   * lageret på -R sælges til samme tilbudspris som originalen, ikke til
   * normalpris og ikke til kampagnens sats.
   */
  setOnCopy?: boolean;
}

/**
 * [D-361] Status for et restparti i prislisten:
 *   - 'planlagt'     kopien findes ikke i BC endnu. DITCO's funktion opretter
 *                    `itemNo` (originalens nummer + "-R") med navnet `name` og
 *                    antallet `maxQuantity` (det disponible på originalen), ikke
 *                    udgået, og sætter den på tilbud. Originalen står til normalpris.
 *   - 'oprettet'     kopien findes i BC (Cockpits seneste varesynk); prisen
 *                    gælder kopien, og `maxQuantity` er kopiens disponible antal.
 *   - 'eksisterende' varen er allerede et restparti på sit eget nummer (navnet
 *                    starter med Restparti); ingen kopi.
 */
export type CampaignRestpartiStatus = 'planlagt' | 'oprettet' | 'eksisterende';

export interface CampaignRestpartiLine {
  status: CampaignRestpartiStatus;
  /** Originalens varenummer (for en kopi nummeret uden "-R"; ellers varen selv). */
  baseItemNo: string;
}

/** Status for restparti-linjerne i en prisliste eller Preview (antal pr. status). */
export interface CampaignRestpartiCounts {
  planlagt: number;
  oprettet: number;
  eksisterende: number;
}

/** Kampagnens prisliste: Resolve-snapshottets varer med den rabat der gælder. */
export interface CampaignPriceList {
  campaign: { publicId: string; name: string; periodStart: string | null; periodEnd: string | null };
  /** Hvornår snapshottet sidst blev lavet (epoch ms); null = Resolve er ikke kørt. */
  resolvedAt: number | null;
  /** [D-357]: kampagnens Alle regler kun på disponibelt lager (hver regel er en lagerregel). */
  stockLimited: boolean;
  /** Hvornår lageret bag `maxQuantity` sidst blev synket fra BC (epoch ms); null = ukendt eller ingen linje har loft. */
  quantityMeasuredAt: number | null;
  /** Reglen for antallet i klar tekst (til DITCO); null når ingen linje har loft. */
  quantityRule: string | null;
  /** [D-359]: hvorfor bundles ikke er i prislisten, i klar tekst (til DITCO). */
  bundleRule: string;
  /** [D-361]: restparti-linjerne pr. status; null når ingen linje er et restparti. */
  restparti: (CampaignRestpartiCounts & { rule: string }) | null;
  /** [D-400]: linjer der beholder en stående rabat; null når ingen gør. */
  standing: { count: number; rule: string } | null;
  lines: CampaignPriceLine[];
}

/**
 * [D-361] Reglen for Kun restparti i klar tekst (Charlie 05-10-2026). ÉT sted,
 * så prislisten, fluebenets tooltip og dokumentationen siger det samme.
 */
export const CAMPAIGN_RESTPARTI_RULE =
  'Kun restparti: rabatten gælder restpartiet, ikke originalen. Et restparti er en vare hvis navn starter med Restparti, eller hvis varenummer ender på -R (en kopi af originalen). På en lagerregel (Kun disponibel eller kampagnens Alle regler) oprettes kopien med det disponible antal: 10 på lager, hvoraf 3 er på ordrer, giver en kopi med 7, og køber kunden 15, er 7 restparti til kampagnepris og 8 af originalen til normalpris. Indtil kopien findes i BC, står den som planlagt med originalens tal, så prognosen er den samme før og efter. Reglens betingelser (leverandør, varegruppe, varenavn) gælder både kopien og originalen, så "Restparti Hvidt …" rammes af Varenavn starter med Hvidt.';

/**
 * [D-359] Bundles får aldrig kampagnepris på eget varenummer (Charlie
 * 05-10-2026): ingen regel rammer en bundle, så den er hverken i Preview,
 * Resolve, prognosen eller prislisten. ÉT sted, så prislisten, kampagnens
 * side og dokumentationen siger det samme.
 */
export const CAMPAIGN_BUNDLE_RULE =
  'Bundles er ikke med i prislisten. En bundle får kampagnepris gennem sine komponenter: i BC står bundle-linjen på salgsordren til 0 kr, og prisen ligger på komponent-linjerne. Bundlen bliver derfor billigere med rabatten på de komponenter der selv er kampagnevarer (fx en vask), mens de øvrige komponenter (fx møblet) står til normalpris. En kampagnepris på bundle-nummeret ville give rabat på hele bundlen.';

/**
 * [D-357] Reglen for kampagner med kun disponibelt lager, som DITCO's kørsel og
 * webshoppen skal håndhæve (Charlie 02-10-2026). ÉT sted, så prislisten,
 * kampagnens side og dokumentationen siger det samme.
 */
export const CAMPAIGN_STOCK_LIMITED_RULE =
  'En lagerregel (fluebenet Kun disponibel, eller alle regler når kampagnen har Alle regler kun på disponibelt lager) gælder kun det disponible lager: beholdning minus antal på salgsordrer. Køber en kunde flere end det disponible antal, får kunden det disponible antal til lagerreglens rabat og resten til normalpris, eller til rabatten fra en regel uden lagerkrav, hvis en sådan også gælder varen (afterMaxQuantity). Eksempel: 10 stk på lager, hvoraf 3 er på eksisterende ordrer, giver 7 disponible; køber kunden 15 stk, er 7 til lagerreglens rabat og 8 til normalpris. En regel uden lagerkrav gælder uanset lager.';

/**
 * [D-400] Reglen for stående rabatter (Charlie 15-10-2026): en kampagne må
 * aldrig hæve en pris. ÉT sted, så prislisten, kampagnens side og
 * dokumentationen siger det samme.
 */
export const CAMPAIGN_STANDING_RULE =
  'Har en vare i forvejen en gældende rabat, der er dybere end kampagnesatsen, beholder varen sin stående rabat, og kampagnen ændrer intet for den. En kampagne må aldrig hæve en pris, og markedsføringslovens førpris ved et besparelsesudsagn er varens laveste pris de seneste 30 dage. Har varen været på tilbud inden for de 30 dage før kampagnestart, beholder den sin tilbudspris, OGSÅ når kampagnesatsen er højere, fordi den ikke må annonceres med en besparelse uden 30 dages normalpris ([D-464]); er tilbuddet sluttet før start, står varen til normalpris. Stående rabatter læses fra restpartiernes aktive rabatlinjer i BC\'s salgsprislister (fx 65 % i S00001, synket dagligt) og for alle varer fra webshoppens gældende tilbudspris. Linjen viser den stående rabat uden antalsloft (den stående rabatlinje har intet maksimum), og DITCO skal hverken oprette kopi eller ændre pris for varen. Findes en -R-kopi allerede, og har kopien ikke selv tilbuddet, står linjen med setOnCopy: DITCO sætter linjens sats på kopien, så lageret på -R sælges til samme tilbudspris som originalen, ikke til normalpris og ikke til kampagnens sats ([D-466]).';

/**
 * [D-476] Varer der aldrig får kampagnepris (Charlie 29-10-2026): ÉN liste på
 * tværs af kampagner i Kampagners tandhjul (feltet `excludedItemRules` på
 * /marketing/campaigns), med samme slags tekstregler som regelsættene på
 * Varer → Lagervarer. Ingen kampagneregel rammer en vare, som mindst 1 af
 * reglerne passer på: hverken i Preview, Resolve, prognosen, prislisten til
 * DITCO eller WP-kategorierne. Erstatter regelsættet, som Kun lagervarer
 * hentede fra Lagervarer, så kampagnerne ikke afhænger af sidens filtre.
 */
export const CAMPAIGN_EXCLUDED_ITEMS_KEY = 'excludedItemRules';

/** Standarden: diverse-numrene pr. leverandør og fragt-kreditoren (fra regelsættet "Black Nov 2026 lagervarer"). */
export const CAMPAIGN_EXCLUDED_ITEMS_DEFAULT: ReadonlyArray<{ field: string; op: 'is'; value: string }> = [
  { field: 'type', op: 'is', value: 'DIVERSE' },
  { field: 'vendor', op: 'is', value: 'Fragt Kreditor - slet ikke!' },
];

/** Feltets hjælp, også de faste undtagelser i koden, så hele listen kan ses ét sted. */
export const CAMPAIGN_EXCLUDED_ITEMS_HELP =
  'Ingen kampagneregel rammer en vare, som mindst 1 af reglerne passer på. Det gælder alle kampagner: Preview, Resolve, prognosen, prislisten til DITCO og WP-kategorierne. Produkttype er varens lagerbogføringsgruppe (fx DIVERSE, diverse-numrene pr. leverandør). Desuden får bundles og fliseprøver aldrig kampagnepris; det er faste regler i Cockpit (bundles får rabatten gennem komponenterne, fliseprøver har fast pris).';

/** Feltets navn i Kampagners tandhjul (også i Black November-analysens kildeangivelse). */
export const CAMPAIGN_EXCLUDED_ITEMS_LABEL = 'Varer der aldrig får kampagnepris';
